<script setup>
import { computed } from 'vue'
import { ESG_ANNEX_C, ESG_NO_DATA, esgEvaluationText } from '@/stores/useEsgStore'

// RED-355: the EVS 875-13:2024 annex C ESG form. Editable in the Expert ESG
// tab, read-only on the Kiirhindamine result page. The form text (factor,
// T/Ä/E, hoonestamata maa, korter, iseloomustus, footnotes) comes from
// resources/js/config/esgAnnexC.json, which the docx export reads too. An
// empty indicator or impact shows — and prints — „info puudub“.
const props = defineProps({
  value: {
    type: Array,
    default: () => [],
  },
  readonly: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['change'])

const columns = ESG_ANNEX_C.columns
const impactVerdicts = ESG_ANNEX_C.impactVerdicts
const footnotes = ESG_ANNEX_C.footnotes

// The form's sub-groups (Veega seonduvad riskid, …) head their first row.
const subgroupStarts = computed(() => {
  const starts = new Set()
  let previous = null
  for (const row of props.value) {
    if (row.subgroup && row.subgroup !== previous) {
      starts.add(row.key)
    }
    previous = row.subgroup ?? null
  }
  return starts
})

const descriptionLines = row => (row.description ? row.description.split('<br>') : [])

const isSelect = row =>
  row.evaluation !== null && typeof row.evaluation === 'object' && 'value' in row.evaluation

const headerClass = 'bg-gray-200 text-sm'
const markerClass = 'text-center whitespace-nowrap text-sm'
</script>

<template>
  <div class="rounded-lg overflow-hidden shadow-md" data-testid="esg-annex-c-table">
    <DataTable
      :value="props.value"
      row-group-mode="subheader"
      group-rows-by="esg.group"
      data-key="key"
      show-gridlines
      size="small"
      scrollable
      table-style="min-width: 72rem"
    >
      <template #empty>
        <div class="flex justify-center items-center h-full">
          <ProgressSpinner style="width: 3rem; height: 3rem" />
        </div>
      </template>

      <Column field="factor" :header="columns.factor" :header-class="headerClass" style="min-width: 16rem">
        <template #body="{ data }">
          <div v-if="subgroupStarts.has(data.key)" class="text-xs italic text-gray-500 mb-1">{{ data.subgroup }}</div>
          <div :data-testid="`esg-annex-c-factor-${data.key}`">{{ data.factor }}</div>
        </template>
      </Column>
      <Column field="use" :header="columns.use" :header-class="headerClass" :body-class="markerClass" />
      <Column field="land" :header="columns.land" :header-class="headerClass" :body-class="markerClass" />
      <Column field="apartment" :header="columns.apartment" :header-class="headerClass" :body-class="markerClass" />
      <Column field="description" :header="columns.description" :header-class="headerClass" body-class="text-xs" style="min-width: 12rem">
        <template #body="{ data }">
          <div v-for="(line, index) in descriptionLines(data)" :key="index">{{ line }}</div>
        </template>
      </Column>
      <Column field="source" :header="columns.source" :header-class="headerClass" style="min-width: 11rem">
        <template #body="{ data }">
          <span v-if="readonly" :class="{ 'text-gray-400': !data.source }">{{ data.source || '-' }}</span>
          <Textarea
            v-else
            v-model="data.source"
            :placeholder="data.suggestedSource"
            :data-testid="`esg-annex-c-source-${data.key}`"
            rows="1"
            auto-resize
            fluid
            @update:model-value="emit('change')"
          />
        </template>
      </Column>
      <Column field="evaluation" :header="columns.evaluation" :header-class="headerClass" style="min-width: 14rem">
        <template #body="{ data }">
          <span
            v-if="readonly"
            :class="{ 'text-gray-400': !esgEvaluationText(data) }"
            :data-testid="`esg-annex-c-evaluation-${data.key}`"
          >{{ esgEvaluationText(data) || ESG_NO_DATA }}</span>
          <Select
            v-else-if="isSelect(data)"
            v-model="data.evaluation.value"
            :options="data.evaluation.options"
            :placeholder="ESG_NO_DATA"
            :data-testid="`esg-annex-c-evaluation-${data.key}`"
            show-clear
            fluid
            @update:model-value="emit('change')"
          />
          <Textarea
            v-else
            v-model="data.evaluation"
            :placeholder="ESG_NO_DATA"
            :data-testid="`esg-annex-c-evaluation-${data.key}`"
            rows="1"
            auto-resize
            fluid
            @update:model-value="emit('change')"
          />
        </template>
      </Column>
      <Column field="impact" :header="columns.impact" :header-class="headerClass" style="min-width: 14rem">
        <template #body="{ data }">
          <template v-if="readonly">
            <div :class="{ 'text-gray-400': !data.impactVerdict }">{{ data.impactVerdict || ESG_NO_DATA }}</div>
            <div v-if="data.impact" class="text-sm">{{ data.impact }}</div>
          </template>
          <div v-else class="flex flex-col gap-2">
            <Select
              v-model="data.impactVerdict"
              :options="impactVerdicts"
              :placeholder="ESG_NO_DATA"
              :data-testid="`esg-annex-c-verdict-${data.key}`"
              show-clear
              fluid
              @update:model-value="emit('change')"
            />
            <Textarea
              v-model="data.impact"
              :placeholder="$t('messages.detail.esgAnnexC.mitigation')"
              :data-testid="`esg-annex-c-mitigation-${data.key}`"
              rows="1"
              auto-resize
              fluid
              @update:model-value="emit('change')"
            />
          </div>
        </template>
      </Column>
      <Column field="reference" :header="columns.reference" :header-class="headerClass" style="min-width: 9rem">
        <template #body="{ data }">
          <span v-if="readonly" :class="{ 'text-gray-400': !data.reference || data.reference === '-' }">{{ data.reference || '-' }}</span>
          <Textarea
            v-else
            v-model="data.reference"
            rows="1"
            auto-resize
            fluid
            @update:model-value="emit('change')"
          />
        </template>
      </Column>

      <template #groupheader="{ data }">
        <div class="font-bold">{{ data.esg.group }}</div>
      </template>
    </DataTable>

    <ul class="px-4 py-3 text-xs text-gray-500 space-y-0.5 bg-white" data-testid="esg-annex-c-footnotes">
      <li v-for="note in footnotes" :key="note">{{ note }}</li>
    </ul>
  </div>
</template>

<style scoped>
  @reference '../../../../css/tailwind.css';

:deep(.p-datatable-row-group-header) {
  @apply bg-gray-100;
}
</style>
